import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, FileText, Settings, Lightbulb, ChevronRight,
  CheckCircle, Shield, Zap, Layers, Database,
  Users, ClipboardList, GitBranch, Lock, Sparkles,
  Upload, Search, Cpu, AlertTriangle, Download, PackageCheck,
  GitMerge, BarChart2
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';
import CustomSolutionModal from '../components/CustomSolutionModal';

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent: 'bg-accent/15 text-amber-700 border border-accent/30',
    dark: 'bg-white/10 text-white/80 border border-white/20',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

export default function Solutions() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const [customModalOpen, setCustomModalOpen] = useState(false);

  const platformFeatures = [
    {
      icon: Users,
      title: lang === 'ar' ? 'الحوكمة والأدوار' : 'Governance & Roles',
      desc: lang === 'ar' ? 'مشرف، قائد، مهندس — صلاحيات دقيقة لكل مستوى' : 'Admin, Leader, Engineer — precise permissions at every level',
      color: 'text-primary',
      bg: 'bg-primary/10',
    },
    {
      icon: ClipboardList,
      title: lang === 'ar' ? 'سجل المراجعة' : 'Audit Trail',
      desc: lang === 'ar' ? 'كل قرار موثق وقابل للتتبع عبر الزمن' : 'Every decision documented and traceable across time',
      color: 'text-success',
      bg: 'bg-emerald-50',
    },
    {
      icon: GitBranch,
      title: lang === 'ar' ? 'سير العمل المتكامل' : 'Unified Workflow Engine',
      desc: lang === 'ar' ? 'تدفق موحد بين الوحدات مع توجيه الاستثناءات' : 'Unified flow across modules with exception routing',
      color: 'text-info',
      bg: 'bg-sky-50',
    },
    {
      icon: Lock,
      title: lang === 'ar' ? 'أمان المؤسسات' : 'Enterprise Security',
      desc: lang === 'ar' ? 'تشفير كامل، تحكم في الوصول، وامتثال معايير البيانات' : 'Full encryption, access control, and data compliance',
      color: 'text-accent',
      bg: 'bg-accent/10',
    },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <CustomSolutionModal isOpen={customModalOpen} onClose={() => setCustomModalOpen(false)} />
      <AIAssistant source="solutions" />

      {/* ── HERO ── */}
      <section className="relative min-h-[72vh] flex items-center overflow-hidden bg-midnight -mt-16 pt-16">
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/12 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/3 w-[300px] h-[300px] rounded-full bg-accent/8 blur-[80px] pointer-events-none" />

        {/* Background image */}
        <div className={`absolute inset-y-0 ${isRTL ? 'left-0' : 'right-0'} w-1/2 hidden lg:block pointer-events-none`}>
          <img
            src="/industrial/engineering drawings 3.jpg"
            alt="Industrial engineering"
            className="w-full h-full object-cover opacity-12"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-transparent to-midnight' : 'from-midnight to-transparent'}`} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="dark"><Layers size={12} />{lang === 'ar' ? 'منصة تولي الصناعية' : 'Tuly Industrial Platform'}</Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[3.2rem] font-black text-white leading-[1.06] tracking-tight"
            >
              {lang === 'ar' ? (
                <>حلول <span className="gradient-text">الذكاء الاصطناعي</span> الصناعية</>
              ) : (
                <>AI <span className="gradient-text">Industrial</span> Solutions</>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl"
            >
              {lang === 'ar'
                ? 'منصة واحدة موحدة تحوّل عمليات قوائم الكميات والمستندات التقنية من فوضى يدوية إلى مخرجات ERP جاهزة — بحوكمة كاملة وتكامل فوري.'
                : 'One unified platform transforming BoQ operations and technical submittals from manual chaos to ERP-ready outputs — with full governance and instant integration.'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
              >
                {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </button>
              <button
                onClick={() => navigate('/platform')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
              >
                {lang === 'ar' ? 'استكشف المنصة' : 'Explore Platform'}
              </button>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ── SOLUTION CARDS ── */}
      <Section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="primary"><Sparkles size={12} />{lang === 'ar' ? 'الوحدات' : 'Modules'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'اختر الوحدة المناسبة' : 'Choose Your Module'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'وحدتان متخصصتان تعملان فوق طبقة حوكمة مشتركة، أو طوّر حلاً مخصصاً لاحتياجاتك.'
                : 'Two specialized modules running over a shared governance layer, or build a custom solution for your needs.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* BoQ Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              onClick={() => navigate('/solutions/boq')}
              className="relative rounded-2xl overflow-hidden border border-border-light bg-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-gradient-to-r from-primary to-blue-500" />
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText size={22} className="text-primary" />
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-primary/8 text-primary text-xs font-bold uppercase tracking-wide">
                    {lang === 'ar' ? 'الوحدة ١' : 'Module 1'}
                  </span>
                </div>
                <h3 className="text-heading font-bold text-xl mb-3">
                  {lang === 'ar' ? 'تحويل قوائم الكميات' : 'BoQ Conversion'}
                </h3>
                <p className="text-body text-sm leading-relaxed mb-5">
                  {lang === 'ar'
                    ? 'حوّل ملفات قوائم الكميات غير المنظمة إلى مخرجات ERP جاهزة بتصنيف آلي وربط بالكتالوج.'
                    : 'Convert unstructured BoQ files into ERP-ready outputs with automated classification and catalog mapping.'}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    lang === 'ar' ? 'يقبل Excel, PDF, Word, وجداول ممسوحة' : 'Accepts Excel, PDF, Word, scanned tables',
                    lang === 'ar' ? 'تصنيف المنتجات ومطابقة الكتالوج بالذكاء الاصطناعي' : 'AI product classification & catalog matching',
                    lang === 'ar' ? 'تصدير مباشر إلى Odoo, SAP, Oracle, Dynamics' : 'Direct export to Odoo, SAP, Oracle, Dynamics',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-primary mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="rounded-xl overflow-hidden border border-border-light mb-5">
                  <img src="/mockup/boq/screen 1.png" alt="BoQ Module" className="w-full group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
                <div className="flex items-center gap-1.5 text-primary text-sm font-semibold">
                  {lang === 'ar' ? 'استكشف الوحدة' : 'Explore Module'}
                  <ArrowRight size={15} className={`group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </motion.div>

            {/* Submittals Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              onClick={() => navigate('/solutions/submittals')}
              className="relative rounded-2xl overflow-hidden border border-border-light bg-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <Settings size={22} className="text-emerald-600" />
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wide">
                    {lang === 'ar' ? 'الوحدة ٢' : 'Module 2'}
                  </span>
                </div>
                <h3 className="text-heading font-bold text-xl mb-3">
                  {lang === 'ar' ? 'المستندات التقنية' : 'Technical Submittals'}
                </h3>
                <p className="text-body text-sm leading-relaxed mb-5">
                  {lang === 'ar'
                    ? 'حوّل عمليات المستندات التقنية إلى سير عمل رقمي محكوم مع تحليل الامتثال الآلي.'
                    : 'Turn technical submittal operations into a controlled digital workflow with automated compliance analysis.'}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    lang === 'ar' ? 'استيعاب المواصفات والدتاشيت تلقائياً' : 'Automatic spec & datasheet ingestion',
                    lang === 'ar' ? 'تحليل الامتثال بالذكاء الاصطناعي' : 'AI-powered compliance analysis',
                    lang === 'ar' ? 'توليد وثائق المستندات والضمانات' : 'Generate submittal docs & warranty letters',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="rounded-xl overflow-hidden border border-border-light mb-5">
                  <img src="/mockup/ts/dashboard_redesign.png" alt="Submittals Module" className="w-full group-hover:scale-[1.03] transition-transform duration-500" />
                </div>
                <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-semibold">
                  {lang === 'ar' ? 'استكشف الوحدة' : 'Explore Module'}
                  <ArrowRight size={15} className={`group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </motion.div>

            {/* Custom Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onClick={() => setCustomModalOpen(true)}
              className="relative rounded-2xl overflow-hidden border border-accent/25 bg-gradient-to-br from-[#FFFCF0] to-white card-hover group cursor-pointer"
            >
              <div className="h-1 bg-gradient-to-r from-accent to-amber-400" />
              <div className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <Lightbulb size={22} className="text-accent" />
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-accent/20 text-amber-700 text-xs font-bold uppercase tracking-wide">
                    {lang === 'ar' ? 'مخصص' : 'Custom'}
                  </span>
                </div>
                <h3 className="text-heading font-bold text-xl mb-3">
                  {lang === 'ar' ? 'حل مخصص' : 'Custom Solution'}
                </h3>
                <p className="text-body text-sm leading-relaxed mb-5">
                  {lang === 'ar'
                    ? 'صف تحديك الخاص ونهندس لك حلاً مبنياً على البنية التحتية ذاتها لتولي مع حوكمة وتكامل كاملين.'
                    : 'Describe your unique challenge and we engineer a solution built on the same Tuly infrastructure with full governance and integration.'}
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    lang === 'ar' ? 'بنية تحتية ثابتة ومعتمدة' : 'Proven infrastructure foundation',
                    lang === 'ar' ? 'تكييف كامل لمتطلباتك' : 'Fully adapted to your requirements',
                    lang === 'ar' ? 'طرح سريع وحوكمة مدمجة' : 'Fast deployment with built-in governance',
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-body">
                      <CheckCircle size={13} className="text-accent mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="rounded-xl bg-accent/8 border border-accent/15 p-8 flex flex-col items-center text-center mb-5">
                  <div className="flex gap-3 mb-4">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        animate={{ y: [0, -8, 0] }}
                        transition={{ duration: 1.6, delay: i * 0.3, repeat: Infinity }}
                        className="w-4 h-4 rounded-full bg-accent/50"
                      />
                    ))}
                  </div>
                  <p className="text-amber-700 text-sm font-medium">
                    {lang === 'ar' ? 'صف تحديك' : 'Describe your challenge'}
                  </p>
                  <p className="text-amber-600/60 text-xs mt-1">
                    {lang === 'ar' ? 'وسنهندس لك الحل' : "We'll engineer the solution"}
                  </p>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); setCustomModalOpen(true); }}
                  className="w-full py-3 rounded-xl bg-accent text-[#1A1A1A] font-semibold text-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
                >
                  {lang === 'ar' ? 'ابدأ المحادثة' : 'Start the Conversation'}
                  <ChevronRight size={15} className={isRTL ? 'rotate-180' : ''} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── PLATFORM LAYER ── */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-primary/5 blur-[100px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="primary"><Shield size={12} />{lang === 'ar' ? 'طبقة المنصة المشتركة' : 'Shared Platform Layer'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'حوكمة موحدة لكلا الوحدتين' : 'Unified Governance Across Both Modules'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'بنية تحتية مشتركة تضمن الاتساق والأمان وقابلية التتبع في كل عملية.'
                : 'Shared infrastructure ensuring consistency, security, and traceability across every operation.'}
            </p>
          </div>

          {/* ── Architecture Diagram ── */}
          <div className="mb-14">
            <div className="bg-slate-50/70 border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">

              {/*
                ALIGNMENT STRATEGY (matches Platform.jsx exactly)
                Both rows use flex + flex-1 children with NO gap.
                Badge centers → 25% and 75%  (SVG x = 250, 750)
                Step centers  → 8.33%, 25%, 41.67%, 58.33%, 75%, 91.67%
                               (SVG x = 83, 250, 417, 583, 750, 917)
              */}

              {/* Module badges — desktop flex-1 */}
              <div className="hidden lg:flex mb-0">
                {[
                  { label: lang === 'ar' ? 'وحدة قوائم الكميات' : 'BoQ MODULE',        icon: FileText, link: '/solutions/boq',        icBg: 'bg-blue-100',  ic: 'text-blue-600'  },
                  { label: lang === 'ar' ? 'وحدة المستندات'    : 'SUBMITTALS MODULE', icon: Settings, link: '/solutions/submittals', icBg: 'bg-amber-100', ic: 'text-amber-600' },
                ].map((m, i) => (
                  <div key={i} className="flex-1 flex justify-center">
                    <motion.button
                      onClick={() => navigate(m.link)}
                      initial={{ opacity: 0, y: -12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group"
                    >
                      <div className={`w-7 h-7 rounded-lg ${m.icBg} flex items-center justify-center flex-shrink-0`}>
                        <m.icon size={14} className={m.ic} />
                      </div>
                      <span className="text-slate-700 font-bold text-sm tracking-wide">{m.label}</span>
                      <ArrowRight size={12} className={`text-slate-400 group-hover:text-slate-600 transition-colors ml-2 ${isRTL ? 'rotate-180' : ''}`} />
                    </motion.button>
                  </div>
                ))}
              </div>

              {/* Mobile badge row */}
              <div className="flex lg:hidden gap-3 mb-4">
                {[
                  { label: lang === 'ar' ? 'وحدة قوائم الكميات' : 'BoQ MODULE',        icon: FileText, link: '/solutions/boq',        icBg: 'bg-blue-100',  ic: 'text-blue-600'  },
                  { label: lang === 'ar' ? 'وحدة المستندات'    : 'SUBMITTALS MODULE', icon: Settings, link: '/solutions/submittals', icBg: 'bg-amber-100', ic: 'text-amber-600' },
                ].map((m, i) => (
                  <button key={i} onClick={() => navigate(m.link)}
                    className="flex-1 flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-700">
                    <div className={`w-5 h-5 rounded-md ${m.icBg} flex items-center justify-center flex-shrink-0`}>
                      <m.icon size={11} className={m.ic} />
                    </div>
                    {m.label}
                  </button>
                ))}
              </div>

              {/* SVG pipeline connector — both modules funnel into Upload, then step-to-step flow */}
              <div className="hidden lg:block relative h-20 pointer-events-none select-none">
                <svg viewBox="0 0 1000 80" className="w-full h-full" preserveAspectRatio="none">
                  {/* BoQ module (x=250) → Upload (x=83) */}
                  <path d="M 250 0 C 250 38 100 42 83 70"
                    fill="none" stroke="#3B82F6" strokeWidth="2.5" strokeLinecap="round" opacity="0.75"/>

                  {/* Submittals module (x=750) → Upload (x=83) */}
                  <path d="M 750 0 C 750 38 100 42 83 70"
                    fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" opacity="0.75"/>

                  {/* Step dots */}
                  {[
                    [83,  '#3B82F6'],
                    [250, '#0EA5E9'],
                    [417, '#06B6D4'],
                    [583, '#10B981'],
                    [750, '#F59E0B'],
                    [917, '#EAB308'],
                  ].map(([x, fill], i) => (
                    <circle key={i} cx={x} cy={72} r={5.5} fill={fill} stroke="white" strokeWidth="2.5"/>
                  ))}
                </svg>
              </div>

              {/* Step cards — desktop flex-1 */}
              <div className="hidden lg:flex mb-8">
                {[
                  { num:'01', labelEn:'Upload',    labelAr:'رفع',     descEn:'BoQ, specs, datasheets',      descAr:'ملفات المدخلات',          icon: Upload,        bg:'bg-blue-50',    ring:'ring-blue-200',    ic:'text-blue-500',    line:'#3B82F6' },
                  { num:'02', labelEn:'Extract',   labelAr:'استخراج', descEn:'Line-item extraction',        descAr:'استخراج بند بند',          icon: Search,        bg:'bg-sky-50',     ring:'ring-sky-200',     ic:'text-sky-500',     line:'#0EA5E9' },
                  { num:'03', labelEn:'Normalize', labelAr:'توحيد',   descEn:'Standardize units & formats', descAr:'توحيد الوحدات والتنسيق',  icon: Cpu,           bg:'bg-cyan-50',    ring:'ring-cyan-200',    ic:'text-cyan-600',    line:'#06B6D4' },
                  { num:'04', labelEn:'Match',     labelAr:'مطابقة',  descEn:'Catalog reconciliation',      descAr:'مطابقة الكتالوج',          icon: Database,      bg:'bg-emerald-50', ring:'ring-emerald-200', ic:'text-emerald-500', line:'#10B981' },
                  { num:'05', labelEn:'Review',    labelAr:'مراجعة',  descEn:'Flag & resolve exceptions',   descAr:'معالجة الاستثناءات',       icon: AlertTriangle, bg:'bg-amber-50',   ring:'ring-amber-200',   ic:'text-amber-500',   line:'#F59E0B' },
                  { num:'06', labelEn:'Generate',  labelAr:'توليد',   descEn:'Unified output creation',     descAr:'إنشاء المخرجات',           icon: Download,      bg:'bg-yellow-50',  ring:'ring-yellow-200',  ic:'text-yellow-600',  line: null    },
                ].map((s, i) => (
                  <React.Fragment key={i}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 + 0.15, duration: 0.5 }}
                      className="flex-1 flex flex-col items-center text-center px-1"
                    >
                      <motion.div
                        whileHover={{ y: -4, scale: 1.07 }}
                        transition={{ duration: 0.2 }}
                        className={`w-14 h-14 rounded-2xl ${s.bg} ring-2 ${s.ring} flex items-center justify-center mb-3 shadow-sm cursor-default`}
                      >
                        <s.icon size={22} className={s.ic} />
                      </motion.div>
                      <div className="flex items-baseline gap-1 justify-center mb-1">
                        <span className={`text-[10px] font-bold ${s.ic} opacity-60`}>{s.num}</span>
                        <span className="text-slate-800 font-bold text-xs">{lang === 'ar' ? s.labelAr : s.labelEn}</span>
                      </div>
                      <p className="text-slate-400 text-[10px] leading-snug max-w-[72px]">{lang === 'ar' ? s.descAr : s.descEn}</p>
                    </motion.div>

                    {s.line && (
                      <div className="flex-shrink-0 flex flex-col items-center pt-7">
                        <svg width="22" height="14" viewBox="0 0 22 14" fill="none">
                          <line x1="0" y1="7" x2="15" y2="7" stroke={s.line} strokeWidth="1.5" strokeLinecap="round"/>
                          <polyline points="11,3 18,7 11,11" stroke={s.line} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile step grid */}
              <div className="grid grid-cols-3 gap-3 mb-6 lg:hidden">
                {[
                  { num:'01', labelEn:'Upload',    icon: Upload,        bg:'bg-blue-50',    ring:'ring-blue-200',    ic:'text-blue-500'    },
                  { num:'02', labelEn:'Extract',   icon: Search,        bg:'bg-sky-50',     ring:'ring-sky-200',     ic:'text-sky-500'     },
                  { num:'03', labelEn:'Normalize', icon: Cpu,           bg:'bg-cyan-50',    ring:'ring-cyan-200',    ic:'text-cyan-600'    },
                  { num:'04', labelEn:'Match',     icon: Database,      bg:'bg-emerald-50', ring:'ring-emerald-200', ic:'text-emerald-500' },
                  { num:'05', labelEn:'Review',    icon: AlertTriangle, bg:'bg-amber-50',   ring:'ring-amber-200',   ic:'text-amber-500'   },
                  { num:'06', labelEn:'Generate',  icon: Download,      bg:'bg-yellow-50',  ring:'ring-yellow-200',  ic:'text-yellow-600'  },
                ].map((s, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <div className={`w-12 h-12 rounded-xl ${s.bg} ring-2 ${s.ring} flex items-center justify-center mb-2`}>
                      <s.icon size={18} className={s.ic} />
                    </div>
                    <span className="text-slate-700 font-bold text-xs">{s.num} {s.labelEn}</span>
                  </div>
                ))}
              </div>

              {/* Platform layer + Output cards */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6">

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="lg:col-span-3 bg-white/90 border border-slate-200 rounded-2xl p-6 shadow-sm"
                >
                  <div className="flex items-center gap-2.5 mb-5">
                    <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Shield size={15} className="text-slate-600" />
                    </div>
                    <span className="text-slate-700 font-bold text-sm uppercase tracking-widest">
                      {lang === 'ar' ? 'طبقة المنصة المشتركة' : 'Shared Platform Layer'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {[
                      { icon: Users,         labelEn: 'Governance & Roles',   labelAr: 'الحوكمة والأدوار'        },
                      { icon: ClipboardList, labelEn: 'Audit Trail',           labelAr: 'سجل المراجعة'            },
                      { icon: BarChart2,     labelEn: 'Confidence Scoring',    labelAr: 'تقييم الثقة'             },
                      { icon: AlertTriangle, labelEn: 'Exception Handling',    labelAr: 'معالجة الاستثناءات'      },
                      { icon: Database,      labelEn: 'ERP Integration',       labelAr: 'تكامل ERP'               },
                      { icon: Sparkles,      labelEn: 'AI Assistant',          labelAr: 'مساعد الذكاء الاصطناعي' },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.06 + 0.2 }}
                        className="flex items-center gap-2.5 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5 hover:border-slate-200 transition-colors"
                      >
                        <item.icon size={13} className="text-slate-500 flex-shrink-0" />
                        <span className="text-slate-600 text-xs font-medium">{lang === 'ar' ? item.labelAr : item.labelEn}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="lg:col-span-2 grid grid-cols-2 gap-3"
                >
                  {[
                    { labelEn:'BoQ Files',    labelAr:'ملفات قوائم الكميات', sub:'Excel · PDF · Word',  icon: FileText,     ic:'text-blue-500',    ibg:'bg-blue-50',    border:'border-blue-100'    },
                    { labelEn:'Catalog Data', labelAr:'بيانات الكتالوج',     sub:'Models & rules',      icon: Database,     ic:'text-cyan-600',    ibg:'bg-cyan-50',    border:'border-cyan-100'    },
                    { labelEn:'ERP Output',   labelAr:'مخرجات ERP',          sub:'Odoo · SAP · Oracle', icon: Layers,       ic:'text-emerald-500', ibg:'bg-emerald-50', border:'border-emerald-100' },
                    { labelEn:'Submittals',   labelAr:'حزم التوصيف',         sub:'Word · PDF',           icon: PackageCheck, ic:'text-amber-500',   ibg:'bg-amber-50',   border:'border-amber-100'   },
                  ].map((out, i) => (
                    <div key={i} className={`bg-white border ${out.border} rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800 font-bold text-sm leading-snug">{lang === 'ar' ? out.labelAr : out.labelEn}</p>
                        <p className="text-slate-400 text-[10px] mt-0.5">{out.sub}</p>
                      </div>
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${out.ibg} flex items-center justify-center`}>
                        <out.icon size={18} className={out.ic} />
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* ERP connector + logos */}
              <div>
                <div className="hidden lg:block relative h-10 pointer-events-none select-none mb-3">
                  <svg viewBox="0 0 1000 40" className="w-full h-full" preserveAspectRatio="none">
                    <path d="M 500 0 Q 350 18 125 36" stroke="#3B82F6" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.65"/>
                    <path d="M 500 0 Q 450 18 375 36" stroke="#06B6D4" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.65"/>
                    <path d="M 500 0 Q 550 18 625 36" stroke="#10B981" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.65"/>
                    <path d="M 500 0 Q 650 18 875 36" stroke="#F59E0B" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.65"/>
                    {[125, 375, 625, 875].map((x, i) => (
                      <circle key={i} cx={x} cy={36} r={4}
                        fill={['#3B82F6','#06B6D4','#10B981','#F59E0B'][i]}
                        stroke="white" strokeWidth="2"/>
                    ))}
                  </svg>
                </div>
                <div className="flex">
                  {[
                    { name: 'odoo',     color: '#714B67' },
                    { name: 'SAP',      color: '#1872BE' },
                    { name: 'ORACLE',   color: '#C74634' },
                    { name: 'Dynamics', color: '#00A4EF' },
                  ].map((erp, i) => (
                    <div key={i} className="flex-1 px-1.5">
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08 + 0.25 }}
                        className="bg-white border border-slate-200 rounded-2xl py-3.5 text-center shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                      >
                        <span className="font-black text-lg" style={{ color: erp.color }}>{erp.name}</span>
                      </motion.div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {platformFeatures.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-border-light card-hover shadow-card"
              >
                <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center mb-4`}>
                  <f.icon size={18} className={f.color} />
                </div>
                <h3 className="text-heading font-bold text-sm mb-2">{f.title}</h3>
                <p className="text-body text-xs leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <button
              onClick={() => navigate('/platform')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-border-light text-heading font-semibold text-sm hover:bg-primary/5 hover:text-primary hover:border-primary/20 transition-all"
            >
              {lang === 'ar' ? 'اعرف المزيد عن المنصة' : 'Learn More About the Platform'}
              <ArrowRight size={15} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>
      </Section>

      {/* ── CTA ── */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="absolute top-0 right-1/3 w-72 h-72 rounded-full bg-primary/5 blur-[80px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'جاهز لتحويل عملياتك؟' : 'Ready to Transform Your Operations?'}
          </h2>
          <p className="mt-4 text-body leading-relaxed text-sm max-w-xl mx-auto">
            {lang === 'ar'
              ? 'احجز عرضاً مباشراً وشاهد كيف تحوّل منصة تولي عمليات قوائم الكميات والمستندات التقنية لديك.'
              : 'Book a live demo and see how Tuly transforms your BoQ and submittal operations end to end.'}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => setCustomModalOpen(true)}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border-2 border-accent/30 text-amber-700 font-semibold hover:bg-accent/8 transition-all"
            >
              <Lightbulb size={16} />
              {lang === 'ar' ? 'حل مخصص' : 'Custom Solution'}
            </button>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
